import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, Share2 } from "lucide-react";
import API from "../lib/api";
import { Section, ArticleCard } from "../components/Common";
import PractitionerRecommendations from "../components/PractitionerRecommendations";
import { toast } from "sonner";

const DoshaArt = ({ type }) => {
  const map = {
    "Vata": { color: "from-[#7AA7E8] to-[#0E1015]", glyph: "∵" },
    "Pitta": { color: "from-[#E2C77A] to-[#7A1F1F]", glyph: "▲" },
    "Kapha": { color: "from-[#4FD1A5] to-[#0E1015]", glyph: "○" },
  };
  const config = map[type?.split("-")[0]] || map.Vata;
  return (
    <div className={`relative h-56 rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br ${config.color}`}>
      <div className="absolute inset-0 grid place-items-center text-[120px] opacity-70 font-display text-white">{config.glyph}</div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_40%_30%,rgba(255,255,255,0.18),transparent_55%)]" />
    </div>
  );
};

export default function DoshaResultPage() {
  const { resultId } = useParams();
  const { t } = useTranslation();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get(`/dosha/results/${resultId}`).then(({ data }) => setR(data)).finally(() => setLoading(false));
  }, [resultId]);

  const share = async () => {
    try {
      const url = window.location.href;
      if (navigator.share) {
        await navigator.share({ title: t("dosha.share_title"), url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success(t("dosha.link_copied"));
      }
    } catch {
      // ignore — fallback when share/clipboard is unavailable
    }
  };

  if (loading) return <div className="app-container py-20 text-white/70">{t("common.loading")}</div>;
  if (!r) return <div className="app-container py-20" data-testid="dosha-no-result">{t("dosha.no_result")}</div>;

  const articles = r.recommendations?.articles || [];

  return (
    <div data-testid="page-dosha-result">
      <section className="hero-bg">
        <div className="app-container pt-14 pb-10">
          <div className="text-xs tracking-[0.32em] uppercase gold-text">— {t("dosha.result_title")}</div>
          <div className="grid md:grid-cols-3 gap-6 items-center mt-3">
            <div className="md:col-span-2">
              <h1 className="font-display text-4xl md:text-6xl" data-testid="result-dosha-type">{r.dosha_type}</h1>
              <div className="text-white/70 mt-2">{t("dosha.modern_body_type")}: <span className="text-white">{r.body_type}</span></div>
              <div className="flex flex-wrap gap-2 mt-4">
                {(r.goals || []).map((g) => <span key={g} className="chip">#{t(`dosha.goals.${g}`, g)}</span>)}
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={share} className="btn-primary inline-flex items-center gap-2" data-testid="share-result"><Share2 className="w-4 h-4" /> {t("dosha.share")}</button>
                <Link to="/ask-donny" className="btn-secondary text-sm" data-testid="dosha-result-ask-donny">{t("nav.ask_donny")}</Link>
              </div>
            </div>
            <DoshaArt type={r.dosha_type} />
          </div>
        </div>
      </section>

      <div className="app-container py-12 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="card-surface p-6" data-testid="result-strengths">
            <div className="text-xs uppercase tracking-[0.24em] gold-text mb-2">{t("dosha.strengths")}</div>
            <ul className="grid sm:grid-cols-2 gap-2 text-white/85">
              {r.strengths.map((s, i) => <li key={i} className="flex gap-2"><span className="gold-text">◆</span>{s}</li>)}
            </ul>
          </div>
          <div className="card-surface p-6" data-testid="result-challenges">
            <div className="text-xs uppercase tracking-[0.24em] gold-text mb-2">{t("dosha.challenges")}</div>
            <ul className="grid sm:grid-cols-2 gap-2 text-white/85">
              {r.challenges.map((s, i) => <li key={i} className="flex gap-2"><span className="gold-text">◆</span>{s}</li>)}
            </ul>
          </div>
          <div className="card-surface p-6" data-testid="result-foods">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.24em] gold-text mb-2">{t("dosha.foods")}</div>
                <ul className="space-y-1 text-white/85">
                  {r.recommended_foods.map((s, i) => <li key={i} className="flex gap-2"><span className="gold-text">·</span>{s}</li>)}
                </ul>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.24em] gold-text mb-2">{t("dosha.avoid")}</div>
                <ul className="space-y-1 text-white/70">
                  {(r.foods_to_avoid || []).map((s, i) => <li key={i} className="flex gap-2"><span className="gold-text">·</span>{s}</li>)}
                </ul>
              </div>
            </div>
          </div>
          <div className="card-surface p-6" data-testid="result-fitness">
            <div className="text-xs uppercase tracking-[0.24em] gold-text mb-2">{t("dosha.fitness")}</div>
            <p className="text-white/85">{r.fitness}</p>
          </div>
          <div className="card-surface p-6" data-testid="result-wellness-recs">
            <div className="text-xs uppercase tracking-[0.24em] gold-text mb-2">{t("dosha.wellness_recs")}</div>
            <ul className="space-y-1 text-white/85">
              {(r.wellness_recommendations || []).map((s, i) => <li key={i} className="flex gap-2"><span className="gold-text">◆</span>{s}</li>)}
            </ul>
          </div>
        </div>
        <aside className="space-y-6">
          <div className="card-surface p-6" data-testid="result-affirmation">
            <div className="text-xs uppercase tracking-[0.24em] gold-text mb-2"><Sparkles className="w-4 h-4 inline mr-1" /> {t("dosha.affirmation")}</div>
            <p className="font-display text-2xl leading-snug">“{r.affirmation}”</p>
            <p className="text-white/55 mt-3 italic">— {r.quote}</p>
          </div>
          {articles.length > 0 && (
            <div className="card-surface p-4" data-testid="result-related">
              <div className="text-xs uppercase tracking-[0.24em] gold-text mb-3 px-2">{t("dosha.related_reads")}</div>
              <div className="grid gap-3">
                {articles.map((a) => (
                  <Link key={a.id} to={`/article/${a.slug}`} className="flex gap-3 p-2 rounded-md hover:bg-white/5">
                    <img src={a.image} alt="" className="w-16 h-16 rounded-md object-cover" />
                    <div>
                      <div className="text-sm leading-tight">{a.title}</div>
                      <div className="text-xs text-white/50 capitalize">{a.category?.replace(/-/g, " ")}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>

      {/* PERSONALIZED PRACTITIONERS — recommended for the user's dosha */}
      <PractitionerRecommendations
        eyebrow={t("dosha.practitioners_eyebrow")}
        title={t("dosha.practitioners_title", { type: r.dosha_type })}
        subtitle={t("dosha.practitioners_subtitle")}
        dosha={(r.dosha_type || "").split("-")[0].toLowerCase()}
        limit={3}
        testid="dosha-practitioner-recs"
      />
    </div>
  );
}
